import '@splidejs/splide/dist/css/splide.min.css';

import { CloseRounded } from '@mui/icons-material';
import { Dialog, type DialogProps, type PaperProps } from '@mui/material';
import type { ImageResponsePagedData } from '@on3/api';
import { Splide, SplideSlide } from '@splidejs/react-splide';
import type { Options } from '@splidejs/splide';
import IconButton from 'components/UI/Buttons/IconButton';
import { useCallback, useEffect, useMemo, useState } from 'react';

import styles from './PhotoDialog.module.scss';

interface IPhotoDialogProps extends DialogProps {
  photoList?: ImageResponsePagedData['list'];
  startingModalIndex: number;
}

const paperProps: PaperProps = {
  elevation: 0,
  className: styles.photoDialogPaper,
};

const splideOptions: Options = {
  perMove: 1,
  perPage: 1,
  rewind: true,
  heightRatio: 0.65,
  lazyLoad: 'nearby',
  preloadPages: 1,
};

export const PhotoDialog = ({
  onClose: onCloseProp,
  open,
  photoList,
  startingModalIndex,
}: IPhotoDialogProps) => {
  const [isOpen, setIsOpen] = useState(open);
  const handleClose = useCallback(
    (e: any) => {
      setIsOpen(false);
      onCloseProp?.(e, 'backdropClick');
    },
    [onCloseProp],
  );
  const options = useMemo(
    () => ({ ...splideOptions, start: startingModalIndex }),
    [startingModalIndex],
  );

  useEffect(() => setIsOpen(open), [open]);

  return (
    (photoList?.length && (
      <Dialog
        PaperProps={paperProps}
        className={styles.photoDialogRoot}
        fullScreen
        onClose={handleClose}
        open={isOpen}
      >
        <div className={styles.topBar}>
          <IconButton color="secondary" onClick={handleClose} size="large">
            <CloseRounded />
          </IconButton>
        </div>
        {/* @ts-expect-error look into Splide typings, because this is working */}
        <Splide className={styles.splide} options={options}>
          {photoList.map(({ domain, height, width, altText, source, key }) => {
            const pHeight = height || 400;
            const pWidth = width || 400;
            const src = `https://${domain}/cdn-cgi/image/height=${pHeight},width=${pWidth},quality=90,fit=cover,gravity=auto${source}`;

            return (
              source && (
                <SplideSlide
                  className={styles.splideSlide}
                  key={`${key}-photoDialog`}
                >
                  <img
                    alt={altText || ''}
                    className={styles.image}
                    data-splide-lazy={src}
                  />
                </SplideSlide>
              )
            );
          })}
        </Splide>
      </Dialog>
    )) ||
    null
  );
};
